import { useState, type ChangeEvent } from 'react';
import { toast } from 'sonner';
import { FileSignature, Plus, Upload, Archive, Eye, ChevronRight } from 'lucide-react';
import {
  useOnboardingDocuments,
  useCreateOnboardingDocument,
  useUploadOnboardingVersion,
  useArchiveOnboardingDocument,
  useUpdateOnboardingDocument,
  type OnboardingDoc,
} from '../../hooks/useOnboardingDocuments';
import { Button } from '../../components/ui/Button';
import { DocumentPreviewModal } from '../../components/onboarding/DocumentPreviewModal';
import { DocSignSettings } from '../../components/onboarding/DocSignSettings';
import { Card, CardSection } from '../../components/ui/Card';
import { PageHeader } from '../../components/ui/PageHeader';
import { Pill } from '../../components/ui/Pill';
import { ApiError } from '../../lib/api';
import { PREFLIGHT_KEYS, PREFLIGHT_LABEL, type PreflightKey } from '@hcc/types';

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);
}

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}

export function OnboardingDocuments() {
  const list = useOnboardingDocuments();
  const create = useCreateOnboardingDocument();
  const upload = useUploadOnboardingVersion();
  const archive = useArchiveOnboardingDocument();
  const [title, setTitle] = useState('');
  const [previewDoc, setPreviewDoc] = useState<OnboardingDoc | null>(null);

  async function onCreate() {
    if (!title.trim()) return;
    try {
      await create.mutateAsync({ key: slug(title), title: title.trim() });
      setTitle('');
      toast.success('Document created');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create');
    }
  }

  async function onUpload(docId: string, file: File, opts: { requires_resign: boolean }) {
    try {
      const pdf_base64 = await fileToBase64(file);
      await upload.mutateAsync({
        id: docId,
        title: file.name.replace(/\.(pdf|docx?|)$/i, ''),
        pdf_base64,
        filename: file.name,
        requires_resign: opts.requires_resign,
      });
      toast.success('New version uploaded');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Upload failed (PDF or Word .doc/.docx only)',
      );
    }
  }

  return (
    <main className="mx-auto max-w-3xl space-y-5 px-4 py-8">
      <PageHeader
        eyebrowIcon={FileSignature}
        eyebrow="Onboarding"
        title="Documents to sign"
        description="Upload the documents new starters read and sign, as PDF or Word (.doc/.docx). Word files are converted to PDF on upload. Replacing one adds a new version; old signatures keep their original."
      />

      <Card>
        <CardSection className="flex flex-wrap items-end gap-2">
          <label className="flex-1 text-sm">
            <span className="font-medium text-fg-muted">New document title</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Code of Conduct"
              className="mt-1 block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm"
            />
          </label>
          <Button variant="primary" size="sm" iconLeft={<Plus className="h-4 w-4" />} onClick={onCreate} loading={create.isPending} disabled={!title.trim()}>
            Create
          </Button>
        </CardSection>
      </Card>

      <div className="space-y-3">
        {(list.data ?? []).filter((d) => !d.archived_at).map((d) => (
          <DocumentTile
            key={d.id}
            doc={d}
            onPreview={() => setPreviewDoc(d)}
            onArchive={async () => {
              if (!confirm(`Archive "${d.title}"?`)) return;
              await archive.mutateAsync(d.id);
            }}
            onUpload={onUpload}
          />
        ))}
        {list.data && list.data.filter((d) => !d.archived_at).length === 0 ? (
          <p className="text-sm text-fg-muted">No documents yet.</p>
        ) : null}
      </div>

      {previewDoc && previewDoc.versions.length > 0 ? (
        <DocumentPreviewModal
          open
          onClose={() => setPreviewDoc(null)}
          doc={previewDoc}
          version={[...previewDoc.versions].sort((a, b) => b.version - a.version)[0]!}
        />
      ) : null}
    </main>
  );
}

/**
 * One document as a collapsible tile: the header (title, status pills, Preview
 * and Archive) is always visible; the settings + upload row reveal on expand.
 * Collapsed by default so a long list stays compact. Preview/Archive sit
 * outside the expand toggle so they work without opening the tile.
 */
function DocumentTile({
  doc,
  onPreview,
  onArchive,
  onUpload,
}: {
  doc: OnboardingDoc;
  onPreview: () => void;
  onArchive: () => void;
  onUpload: (docId: string, file: File, opts: { requires_resign: boolean }) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card>
      <CardSection className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
          >
            <ChevronRight
              className={`h-4 w-4 shrink-0 text-fg-dim transition-transform ${expanded ? 'rotate-90' : ''}`}
              aria-hidden
            />
            <div className="min-w-0">
              <div className="truncate font-medium text-fg">{doc.title}</div>
              <div className="mt-1 flex flex-wrap items-center gap-1">
                {doc.versions.length > 0 ? (
                  <Pill tone="neutral" size="sm">v{Math.max(...doc.versions.map((v) => v.version))} current</Pill>
                ) : (
                  <Pill tone="warning" size="sm">No version uploaded</Pill>
                )}
                <span className="text-xs text-fg-dim">{doc.versions.length} version(s)</span>
                {doc.requires_option ? <Pill tone="accent" size="sm">Choice</Pill> : null}
                {doc.requires_approval || doc.preflight_key ? <Pill tone="info" size="sm">Sign-off</Pill> : null}
                {doc.preflight_key ? <Pill tone="accent" size="sm">Gate: {PREFLIGHT_LABEL[doc.preflight_key]}</Pill> : null}
              </div>
            </div>
          </button>
          <div className="flex shrink-0 items-center gap-1">
            {doc.versions.length > 0 ? (
              <Button variant="ghost" size="sm" iconLeft={<Eye className="h-4 w-4" />} onClick={onPreview}>
                Preview
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" iconLeft={<Archive className="h-4 w-4" />} onClick={onArchive}>
              Archive
            </Button>
          </div>
        </div>
        {expanded ? (
          <>
            <DocGateControls doc={doc} />
            <DocSignSettings doc={doc} />
            <UploadRow docId={doc.id} hasVersions={doc.versions.length > 0} onUpload={onUpload} />
          </>
        ) : null}
      </CardSection>
    </Card>
  );
}

/**
 * HR approval + pre-employment gate controls for one document, independent of the
 * uploaded PDF versions. A document that satisfies a pre-employment gate always
 * needs sign-off, so the approval toggle is forced on in that case.
 */
function DocGateControls({ doc }: { doc: OnboardingDoc }) {
  const update = useUpdateOnboardingDocument();
  const gated = !!doc.preflight_key;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line/10 pt-3 text-sm">
      <label className="flex items-center gap-2 text-fg">
        <input
          type="checkbox"
          checked={doc.requires_approval || gated}
          disabled={gated || update.isPending}
          onChange={(e) => update.mutate({ id: doc.id, requires_approval: e.target.checked })}
        />
        Needs HR sign-off after signing
      </label>
      <label className="flex items-center gap-2 text-fg-muted">
        <span>Satisfies pre-employment</span>
        <select
          value={doc.preflight_key ?? ''}
          disabled={update.isPending}
          onChange={(e) =>
            update.mutate({
              id: doc.id,
              preflight_key: (e.target.value || null) as PreflightKey | null,
            })
          }
          className="rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1 text-sm"
        >
          <option value="">None</option>
          {PREFLIGHT_KEYS.map((k) => (
            <option key={k} value={k}>{PREFLIGHT_LABEL[k]}</option>
          ))}
        </select>
      </label>
    </div>
  );
}

function UploadRow({
  docId,
  hasVersions,
  onUpload,
}: {
  docId: string;
  hasVersions: boolean;
  onUpload: (docId: string, file: File, opts: { requires_resign: boolean }) => void;
}) {
  const [requiresResign, setRequiresResign] = useState(false);

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) onUpload(docId, file, { requires_resign: requiresResign });
    e.target.value = '';
  }

  return (
    <div className="space-y-2 rounded-token-sm bg-surface-2 p-3">
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-accent hover:underline">
          <Upload className="h-4 w-4" />
          {hasVersions ? 'Upload new version' : 'Upload PDF or Word'}
          <input
            type="file"
            accept="application/pdf,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            onChange={onPick}
          />
        </label>
        {hasVersions ? (
          <label className="flex items-center gap-1 text-xs text-fg-muted">
            <input type="checkbox" checked={requiresResign} onChange={(e) => setRequiresResign(e.target.checked)} />
            Require everyone to re-sign
          </label>
        ) : null}
      </div>
    </div>
  );
}
